// ============================================================================
// CONFIDENCE BREAKDOWN — assembles a human-readable explanation of why a
// signal's displayed confidence is what it is.
//
// Walks the pipeline backwards: cluster contributions → raw heuristic score
// → Bayesian-shrunk calibration → backtest result → reliability-curve
// remap → displayed value.
//
// Two outputs:
//   buildConfidenceTitle(signal) → multi-line string suitable for a
//     native `title` attribute tooltip. Works on every browser, supports
//     mobile long-press, no popover library required.
//   buildConfidenceBreakdown(signal) → structured object the same helper
//     uses internally; exported in case a future richer popover wants it.
//
// Designed to fail gracefully — every section guards on the field being
// present, so a partially-built signal (mid-validation, no backtest yet,
// pre-calibration deploys) still produces a useful tooltip.
// ============================================================================

const CLUSTER_LABELS = {
  momentum:   'Momentum',
  trend:      'Trend',
  volatility: 'Volatility',
  volume:     'Volume',
  structure:  'Structure',
  macro:      'Macro',
};

function fmtSigned(n) {
  if (!Number.isFinite(n)) return '0';
  if (n > 0) return `+${n.toFixed(n % 1 === 0 ? 0 : 1)}`;
  if (n < 0) return n.toFixed(n % 1 === 0 ? 0 : 1);
  return '0';
}

function buildConfidenceBreakdown(signal) {
  if (!signal) return null;

  const cc = signal.clusterContribs || {};
  const reasons = Array.isArray(signal.reasons) ? signal.reasons : [];

  // Group reasons under their cluster when we can guess; the scorer doesn't
  // tag reasons by cluster, but the strings tend to be cluster-specific
  // ("RSI overbought" → momentum, "MACD bullish" → momentum, etc.). The
  // mapping is best-effort — anything that doesn't match goes to a
  // generic "Other" bucket which we don't render separately. The cluster
  // contribution numbers (cc) are the source of truth; reasons are just
  // colour.
  const clusterRows = Object.entries(CLUSTER_LABELS).map(([key, label]) => ({
    key, label,
    contrib: cc[key] != null ? Number(cc[key]) : 0,
  }));

  const heuristicScore = clusterRows.reduce((s, r) => s + r.contrib, 0);

  // Pull every available stage of the confidence pipeline. Fields that
  // don't exist on this signal are null and the renderer skips that row.
  const stages = {
    heuristic:      Number.isFinite(signal.confidence) && !signal.preCurveConfidence
                       ? Math.round(signal.confidence) : null,
    preCurve:       Number.isFinite(signal.preCurveConfidence)
                       ? Math.round(signal.preCurveConfidence) : null,
    bayesianShrunk: Number.isFinite(signal.calibratedConfidence) &&
                    signal.calibratedConfidence !== signal.confidence &&
                    !signal.preCurveConfidence
                       ? Math.round(signal.calibratedConfidence) : null,
    displayed:      Number.isFinite(signal.confidence)
                       ? Math.round(signal.confidence) : null,
  };

  const backtest = signal._backtestResult ? {
    trials:       signal._backtestResult.trials,
    wins:         signal._backtestResult.wins,
    losses:       signal._backtestResult.losses,
    winRatePct:   signal._backtestResult.winRate,
    sharpe:       signal._backtestResult.sharpe,
    profitFactor: signal._backtestResult.profitFactor,
  } : null;

  const bin = signal.reliabilityBin ? {
    range:           `${signal.reliabilityBin.binLow}-${signal.reliabilityBin.binHigh}`,
    n:               signal.reliabilityBin.n,
    observedWrPct:   signal.reliabilityBin.observedWrPct,
    identityMapped:  !!signal.reliabilityBin.identityMapped,
  } : null;

  return {
    clusterRows,
    heuristicScore,
    stages,
    backtest,
    bin,
    reasons,
    direction: signal.direction,
    riskReward: signal.riskReward,
    source: signal._confidenceSource || 'heuristic',
  };
}

export { buildConfidenceBreakdown };

/**
 * Build a multi-line `title=` tooltip string. Looks roughly like:
 *
 *   Score 4 (heuristic 54% → backtest 71% → curve 67%)
 *
 *   Cluster contributions:
 *     Momentum   +2
 *     Trend      +1
 *     Volume     +1
 *     Macro      −1   Short flow disagrees
 *
 *   Reasons: RSI oversold (28); MACD bullish cross; OBV rising
 *
 *   Validation:
 *     Backtest 12 trials · WR 67% · Sharpe 0.85
 *     Reliability bin 65-70 · n=43 · observed WR 67%
 *
 *   Displayed: 67%
 */
export function buildConfidenceTitle(signal) {
  const b = buildConfidenceBreakdown(signal);
  if (!b) return '';

  const lines = [];

  // Headline — score + the most informative stage transition we can show.
  const flow = [];
  if (b.stages.heuristic   != null) flow.push(`heuristic ${b.stages.heuristic}%`);
  if (b.stages.bayesianShrunk != null) flow.push(`shrunk ${b.stages.bayesianShrunk}%`);
  if (b.stages.preCurve    != null) flow.push(`backtest ${b.stages.preCurve}%`);
  if (b.stages.displayed   != null && (flow.length === 0 || b.stages.displayed !== b.stages[Object.keys(b.stages).filter(k => b.stages[k] != null).slice(-2)[0]]))
    flow.push(`curve ${b.stages.displayed}%`);
  lines.push(`Score ${b.heuristicScore} (${flow.join(' → ')})`);
  lines.push('');

  // Cluster contributions — pad labels so the values line up monospace-ish.
  const nonZero = b.clusterRows.filter(r => r.contrib !== 0);
  if (nonZero.length > 0) {
    lines.push('Cluster contributions:');
    const labelWidth = Math.max(...nonZero.map(r => r.label.length));
    for (const r of nonZero) {
      lines.push(`  ${r.label.padEnd(labelWidth)}  ${fmtSigned(r.contrib)}`);
    }
    lines.push('');
  }

  // Reasons — first ~5 so the tooltip doesn't grow unbounded.
  if (b.reasons.length > 0) {
    lines.push(`Reasons: ${b.reasons.slice(0, 5).join('; ')}`);
    lines.push('');
  }

  // Validation stage — backtest + curve, when present.
  if (b.backtest || b.bin) {
    lines.push('Validation:');
    if (b.backtest) {
      lines.push(`  Backtest ${b.backtest.trials} trials · WR ${b.backtest.winRatePct}% · Sharpe ${b.backtest.sharpe}`);
    }
    if (b.bin) {
      const wr = b.bin.observedWrPct != null ? `observed WR ${Math.round(b.bin.observedWrPct)}%` : 'identity-mapped';
      lines.push(`  Reliability bin ${b.bin.range} · n=${b.bin.n} · ${wr}`);
    }
    lines.push('');
  }

  if (b.stages.displayed != null) {
    lines.push(`Displayed: ${b.stages.displayed}%`);
  }

  // Trim any trailing blank line.
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  return lines.join('\n');
}
